import PermissionButton from 'components/buttons/PermissionButton';
import Loader from 'components/Loader';
import ExpenseModal from 'components/modals/expenses/ExpenseModal';
import { useModal } from 'components/modals/useModal';
import { Tooltip } from 'components/ui/tooltip';
import useAuth from 'hooks/useAuth.ts';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { ClassifierData } from 'types/classifiers';
import { EmployeeListItemMinDto } from 'types/employees';
import { ExpenseDto, ExpenseForm, ExpenseFormData, ExpenseStatusType } from 'types/expenses';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useExpenseEntryDisabled } from 'utils/useExpenseEntryDisabled';
import EffortExpenseItem from './EffortExpenseIItem';

type Props = {
    classifiers: Record<string, ClassifierData>;
    disableAdd?: boolean;
    onChange?: (args: ExpenseDto[]) => void;
    expenseFormDataList: ExpenseFormData[];
    setExpenseFormDataList: (val: ExpenseFormData[]) => void;
};

const EffortExpenseList: React.FC<Props> = ({
    classifiers,
    disableAdd = false,
    onChange,
    expenseFormDataList,
    setExpenseFormDataList,
}) => {
    const { user } = useAuth();
    const userId = user?.id;
    const hasPermissionToUpdateEfforts = useHasPermissions(['UPDATE.EFFORTS.ALL']);
    const isExpenseEntryDisabled = useExpenseEntryDisabled();

    const { data: currentEmployeeQueryData, isLoading: isLoadingCurrentEmployeeQueryData } =
        useGetEmployeesListWithFiltersQuery({ pageSize: 1, page: 0, filters: `id==${userId}` }, { skip: !userId });

    const currentEmployeeData = useMemo<EmployeeListItemMinDto | undefined>(() => {
        return currentEmployeeQueryData?.content.at(0);
    }, [currentEmployeeQueryData]);

    const { data: tenant } = useGetCurrentTenantQuery();

    const { openModal, closeModal } = useModal();

    const updateExpenseList = useCallback(
        (newExpense_: ExpenseForm, id?: number) => {
            const newExpense = mapFormToOldFormDto(newExpense_, id);
            let alreadyExists = false;
            let orderId = 0;
            const newExpenses = expenseFormDataList.map((exp) => {
                orderId += 1;
                if (
                    (exp?.id && id && exp?.id === id) ||
                    (exp?.orderId && newExpense?.orderId && exp?.orderId === newExpense?.orderId)
                ) {
                    alreadyExists = true;
                    return { ...newExpense, orderId: orderId };
                }
                return { ...exp, orderId: orderId };
            });

            if (!alreadyExists) {
                orderId += 1;
                newExpenses.push({ ...newExpense, orderId });
            }

            setExpenseFormDataList(newExpenses);
            if (onChange) onChange(mapFormDataToDto(newExpenses));
        },
        [expenseFormDataList, onChange, setExpenseFormDataList]
    );

    const openExpenseModal = useCallback(
        (expense?: ExpenseDto & { orderId?: number }): void => {
            if (!expense && isExpenseEntryDisabled) {
                return;
            }
            const preSelectedEmployee = currentEmployeeData;

            const initalData: ExpenseForm = {
                id: expense?.id,
                externalId: expense?.externalId || null,
                expenseType:
                    (
                        getOptionsFromClassifier(classifiers['EXPENSE_TYPE']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === expense?.expenseTypeCode) ?? null,
                currencyType:
                    (
                        getOptionsFromClassifier(classifiers['CURRENCY']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === expense?.originalCurrencyTypeCode) ?? null,
                description: expense?.description ?? '',
                expenseAmount: expense?.expenseAmountOriginalCurrency ?? '',
                expenseDate: expense?.expenseDate
                    ? formatDateStringInput(new Date(expense?.expenseDate))
                    : formatDateStringInput(new Date()),
                isNonBillable:
                    expense?.isNonBillable !== undefined && expense?.isNonBillable !== null
                        ? expense.isNonBillable
                        : true,
                employee: expense?.employee
                    ? expense.employee
                    : preSelectedEmployee
                      ? { id: preSelectedEmployee?.id, name: preSelectedEmployee?.name }
                      : null,
                matter: null, // possibly prefilled at ExpenseModal component level
                effortId: expense?.effortId ?? null,
                timecardId: null,
                orderId: expense?.orderId ?? undefined,
            };

            const isReadOnly: boolean =
                !!expense &&
                (isExpenseEntryDisabled ||
                    !hasPermissionToUpdateEfforts ||
                    (!!expense.expenseStatusTypeCode && expense.expenseStatusTypeCode !== ExpenseStatusType.FINISHED));

            openModal(
                <ExpenseModal
                    onChangeRaw={(val) => updateExpenseList(val, expense?.id)}
                    initialData={initalData}
                    onClose={closeModal}
                    titleId={expense ? 'expenses.edit' : 'expenses.add'}
                    readOnly={isReadOnly || disableAdd}
                    hideSelectMatter
                    tenant={tenant}
                    preselectMatterId={expense?.matter?.id ?? undefined}
                    forceNonBillable
                />,
                closeModal
            );
        },
        [
            classifiers,
            disableAdd,
            updateExpenseList,
            closeModal,
            openModal,
            currentEmployeeData,
            tenant,
            hasPermissionToUpdateEfforts,
            isExpenseEntryDisabled,
        ]
    );

    const addButton = (
        <span>
            <PermissionButton
                permissions={['CREATE.EXPENSES']}
                variant="outlined"
                className="w-fit"
                size="small"
                onClick={() => openExpenseModal()}
                disabled={isExpenseEntryDisabled}
            >
                <FormattedMessage id="expenses.add-new-expense" />
            </PermissionButton>
        </span>
    );

    if (isLoadingCurrentEmployeeQueryData) {
        return <Loader />;
    }

    return (
        <div className="flex w-full flex-col gap-4">
            {expenseFormDataList.map((expense, i) => (
                <EffortExpenseItem
                    // oxlint-disable-next-line react-doctor/no-array-index-as-key -- newly created expenses may have no id; index appended for uniqueness
                    key={`${expense.id ?? expense.orderId ?? 'new'}-${i}`}
                    classifiers={classifiers}
                    expense={expense}
                    readOnly={!hasPermissionToUpdateEfforts || isExpenseEntryDisabled}
                    disableView={disableAdd}
                    openExpenseModal={openExpenseModal}
                />
            ))}
            {!disableAdd &&
                (isExpenseEntryDisabled ? (
                    <Tooltip content={t('expenses.integration-entry-disabled')}>{addButton}</Tooltip>
                ) : (
                    addButton
                ))}
        </div>
    );
};

export default EffortExpenseList;

const mapFormDataToDto = (expenses: ExpenseFormData[]): ExpenseDto[] =>
    expenses.map((expense) => {
        const { matterLabel, employeeLabel, expenseTypeCodeLabel, currencyTypeCodeLabel, ...rest } = expense;
        return { ...rest };
    });

const mapFormToOldFormDto = (expense: ExpenseForm, id?: number): ExpenseFormData => {
    return {
        id: id ?? 0,
        description: expense.description,
        isNonBillable:
            expense?.isNonBillable !== undefined && expense?.isNonBillable !== null ? expense.isNonBillable : false,
        matter: expense?.matter ?? null,
        timecardId: expense?.timecardId ?? null,
        employeeId: expense?.employee?.id ?? null,
        expenseTypeCode: expense?.expenseType?.value ?? undefined,
        expenseAmountOriginalCurrency: expense?.expenseAmount ?? null,
        originalCurrencyTypeCode: expense?.currencyType?.value ?? undefined,
        expenseDate: expense.expenseDate ?? new Date().toDateString(),
        orderId: expense?.orderId ?? undefined,
        effortId: expense?.effortId ?? null,
        employee: expense?.employee ?? undefined,
    };
};
